import { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useNFLLeagueOptional } from '@/contexts/NFLLeagueContext';
import { Clock, ArrowRightLeft, UserPlus, Bomb, Filter, Skull, Trophy, RefreshCw, Activity } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { BonusIcon } from './bonuses/BonusRow';
import NFLWaiverClaimModal from './NFLWaiverClaimModal';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface ActivityRow {
  id: string;
  activity_type: string;
  title: string;
  description: string;
  metadata: any;
  created_at: string;
}

interface BonusAwardRow {
  id: string;
  league_id: string;
  amount: number | string;
  awarded_for_week: number | null;
  awarded_at: string;
  reason: string | null;
  recipient_team_id: string;
  recipient_user_id: string;
  trigger_type: string;
  bonus: { bonus_name: string; icon: string | null } | null;
  team: { name: string } | null;
}

interface SurvivorElimRow {
  id: string;
  league_id: string;
  team_id: string;
  eliminated_week: number;
  fantasy_score: number | string | null;
  eliminated_at: string;
  team: { name: string } | null;
}

type FeedItem =
  | {
      kind: 'tx';
      id: string;
      activityType: string;
      title: string;
      description: string;
      week: number | null;
      createdAt: string;
      // SAB-1208: the feed row has always CARRIED these -- nfl-process-waivers writes
      // metadata.player_id on every awarded claim -- and the mapping below dropped them on
      // the floor, reading only metadata.week. They are what makes the row clickable.
      waiverPlayerId: string | null;
      waiverRunDate: string | null;
    }
  | {
      kind: 'bonus_award';
      id: string;
      bonusName: string;
      icon: string;
      teamId: string;
      teamName: string;
      amount: number;
      awardedForWeek: number | null;
      reason: string | null;
      awardedAt: string;
      isManual: boolean;
    }
  | {
      kind: 'survivor_eliminated';
      id: string;
      teamId: string;
      teamName: string;
      eliminatedWeek: number;
      fantasyScore: number | null;
      eliminatedAt: string;
    };

// SAB-512 Phase 5: Lucide icons (non-nav mobile chrome; desktop nav keeps emoji).
const FILTER_TYPES = [
  { id: 'all', label: 'All', icon: Filter },
  { id: 'bonus', label: 'Bonus', icon: Trophy },
  { id: 'add_drop', label: 'Add/Drop', icon: RefreshCw },
  { id: 'trade', label: 'Trades', icon: ArrowRightLeft },
  { id: 'sabotage', label: 'Sabotage', icon: Bomb },
] as const;

type FilterType = typeof FILTER_TYPES[number]['id'];

/**
 * activity_type -> badge. Explicit, because the previous fallback asserted "Add/Drop" for
 * every unlisted type rather than admitting it did not know (SAB-1055).
 *
 * 'trade' is the value an EXECUTED trade writes -- see _shared/trade-feed.ts. Proposals,
 * declines, counters and vetoes write no feed row at all, by design: negotiations are
 * private. If a trade_* row ever appears here again, that is the bug, not a missing badge.
 */
const ACTIVITY_BADGES: Record<string, { Icon: LucideIcon; label: string; accent: string; bg: string }> = {
  trade:      { Icon: ArrowRightLeft, label: 'Trade',      accent: 'text-blue-400',    bg: 'bg-blue-500/10' },
  sabotage:   { Icon: Bomb,           label: 'Sabotage',   accent: 'text-amber-400',   bg: 'bg-amber-500/10' },
  add_drop:   { Icon: UserPlus,       label: 'Add/Drop',   accent: 'text-emerald-400', bg: 'bg-emerald-500/10' },
};

const PAGE_SIZE = 5;
const FETCH_LIMIT = 15;

/* ------------------------------------------------------------------ */
/*  Team avatar / color helpers (deterministic from id + name).        */
/*  Duplicated from NFLBonusesSection — extract to a shared util if    */
/*  a third caller appears.                                            */
/* ------------------------------------------------------------------ */

const TEAM_COLOR_PALETTE = [
  '#F97316', '#EF4444', '#22C55E', '#3B82F6', '#A855F7',
  '#EAB308', '#EC4899', '#14B8A6', '#F472B6', '#94A3B8',
];

function hashId(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h << 5) - h + s.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
}

function teamAvatar(name: string): string {
  const cleaned = name.replace(/[^A-Za-z0-9]/g, '');
  return (cleaned.slice(0, 2) || 'TM').toUpperCase();
}

function teamColor(id: string): string {
  return TEAM_COLOR_PALETTE[hashId(id) % TEAM_COLOR_PALETTE.length];
}

function fmtUSD(n: number): string {
  return '$' + n.toLocaleString('en-US', {
    minimumFractionDigits: n % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

/**
 * SAB-288 Phase 5 — extends the existing NFL activity feed with bonus
 * awards and Survivor eliminations.
 *
 * Merges three sources client-side rather than denormalising into
 * nfl_activity_feed at write time. Trade-off: extra reads, but the
 * canonical source-of-truth for each data type stays single-writer
 * (RPC owns bonus_awards, cron owns survivor_eliminations).
 *
 * `justAwardedIds` is a Phase 7 hand-off seam: when the explosion
 * reveal overlay lands, it'll feed item ids here so freshly-awarded
 * cards get the JUST NOW glow on the feed. Phase 5 just exposes the
 * prop; default empty.
 */
export default function NFLActivityFeed({
  leagueId: leagueIdProp,
  justAwardedIds = [],
}: {
  leagueId?: string;
  justAwardedIds?: string[];
} = {}) {
  // Optional context: NFLHomePage renders this inside NFLLeagueProvider, so
  // currentLeague is available. /nfldemo renders without the provider and
  // passes leagueId as a prop. Either path resolves to a single leagueId.
  const ctx = useNFLLeagueOptional();
  const leagueId = leagueIdProp ?? ctx?.currentLeague?.id ?? null;

  const [filter, setFilter] = useState<FilterType>('all');
  const [activities, setActivities] = useState<ActivityRow[]>([]);
  const [bonusAwards, setBonusAwards] = useState<BonusAwardRow[]>([]);
  const [survivorElims, setSurvivorElims] = useState<SurvivorElimRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  // SAB-1208: held HERE, local to the feed, not in the shell. The modal opens over the page
  // the user is already on -- no tab switch, no shell state, and nothing that can go stale
  // behind a back-navigation.
  const [claim, setClaim] = useState<{ playerId: string; runDate: string | null } | null>(null);

  useEffect(() => {
    if (!leagueId) {
      setIsLoading(false);
      return;
    }
    let cancelled = false;

    async function load() {
      setIsLoading(true);

      // Existing transactions feed.
      let txQuery = (supabase
        .from('nfl_activity_feed' as any)
        .select('id, activity_type, title, description, metadata, created_at')
        .eq('league_id', leagueId)
        .order('created_at', { ascending: false })
        .limit(FETCH_LIMIT)) as any;
      if (filter !== 'all' && filter !== 'bonus') {
        txQuery = txQuery.eq('activity_type', filter);
      }

      // SAB-288 sources.
      const bonusQuery = supabase
        .from('bonus_awards' as any)
        .select(
          `
          id, league_id, amount, awarded_for_week, awarded_at, reason,
          recipient_team_id, recipient_user_id, trigger_type,
          bonus:league_bonuses!bonus_id(bonus_name, icon),
          team:nfl_teams!recipient_team_id(name)
        `,
        )
        .eq('league_id', leagueId)
        .order('awarded_at', { ascending: false })
        .limit(FETCH_LIMIT);

      const survQuery = supabase
        .from('survivor_eliminations' as any)
        .select(
          `
          id, league_id, team_id, eliminated_week, fantasy_score, eliminated_at,
          team:nfl_teams!team_id(name)
        `,
        )
        .eq('league_id', leagueId)
        .order('eliminated_at', { ascending: false })
        .limit(FETCH_LIMIT);

      // 'add_drop' / 'trade' / 'sabotage' filters skip the bonus + survivor
      // queries entirely. 'all' and 'bonus' include them.
      const skipBonusSurvivor =
        filter === 'add_drop' || filter === 'trade' || filter === 'sabotage';

      const [txRes, bonusRes, survRes] = await Promise.all([
        txQuery,
        skipBonusSurvivor
          ? Promise.resolve({ data: [], error: null })
          : bonusQuery,
        skipBonusSurvivor
          ? Promise.resolve({ data: [], error: null })
          : survQuery,
      ]);

      if (cancelled) return;

      setActivities(((txRes as any).data as ActivityRow[]) || []);
      setBonusAwards(((bonusRes as any).data as BonusAwardRow[]) || []);
      setSurvivorElims(((survRes as any).data as SurvivorElimRow[]) || []);
      setVisibleCount(PAGE_SIZE);
      setIsLoading(false);
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [leagueId, filter]);

  /* Merge + sort all three sources. Bonus filter narrows to bonus + surv;
     all filter shows everything; specific tx filters skip bonus/surv. */
  const merged = useMemo<FeedItem[]>(() => {
    const items: FeedItem[] = [];

    if (filter === 'all' || filter !== 'bonus') {
      for (const a of activities) {
        items.push({
          kind: 'tx',
          id: a.id,
          activityType: a.activity_type,
          title: a.title,
          description: a.description,
          week: a.metadata?.week ?? null,
          createdAt: a.created_at,
          // Only a WAIVER add_drop row opens the claim modal. An ordinary free-agent add
          // writes metadata.player_id too (useAddDrop), and it has no waiver log to show --
          // so the transaction_type is what gates it, not the presence of an id.
          waiverPlayerId: a.metadata?.transaction_type === 'waiver'
            ? (a.metadata?.player_id ?? null)
            : null,
          // SAB-1208 stamps run_date at insert. Rows written before it do not carry one and
          // fall back to the player's newest run -- see pickRunGroup.
          waiverRunDate: a.metadata?.run_date ?? null,
        });
      }
    }

    if (filter === 'all' || filter === 'bonus') {
      for (const b of bonusAwards) {
        items.push({
          kind: 'bonus_award',
          id: b.id,
          bonusName: b.bonus?.bonus_name ?? 'Bonus',
          icon: b.bonus?.icon ?? 'trophy',
          teamId: b.recipient_team_id,
          teamName: b.team?.name ?? 'Team',
          amount: Number(b.amount),
          awardedForWeek: b.awarded_for_week,
          reason: b.reason,
          awardedAt: b.awarded_at,
          isManual: b.trigger_type === 'manual',
        });
      }
      for (const e of survivorElims) {
        items.push({
          kind: 'survivor_eliminated',
          id: e.id,
          teamId: e.team_id,
          teamName: e.team?.name ?? 'Team',
          eliminatedWeek: e.eliminated_week,
          fantasyScore:
            e.fantasy_score == null ? null : Number(e.fantasy_score),
          eliminatedAt: e.eliminated_at,
        });
      }
    }

    items.sort((a, b) => tsOf(b).localeCompare(tsOf(a)));
    return items;
  }, [activities, bonusAwards, survivorElims, filter]);

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    const month = d.toLocaleString('en-US', { month: 'short' });
    const day = d.getDate();
    return `${month} ${day}`;
  };

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-base font-bold font-display text-foreground flex items-center gap-2">
          <Clock className="w-4 h-4 text-primary" />
          Activity
        </h2>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-1.5 overflow-x-auto no-scrollbar">
        {FILTER_TYPES.map(f => (
          <Button
            key={f.id}
            variant={filter === f.id ? 'default' : 'outline'}
            size="sm"
            className={cn(
              'text-xs gap-1 h-7 px-2 py-1 shrink-0',
              filter === f.id && 'shadow-sm',
            )}
            onClick={() => setFilter(f.id)}
          >
            <f.icon className="w-3.5 h-3.5" />
            {f.label}
          </Button>
        ))}
      </div>

      {/* Feed */}
      <Card className="bg-card border-border overflow-hidden">
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="h-5 w-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : merged.length === 0 ? (
            <EmptyFeed filter={filter} />
          ) : (
            <>
              {merged.slice(0, visibleCount).map(item => (
                <FeedRow
                  key={`${item.kind}:${item.id}`}
                  item={item}
                  freshlyAwarded={justAwardedIds.includes(item.id)}
                  formatDate={formatDate}
                  onOpenWaiverClaim={(playerId, runDate) => setClaim({ playerId, runDate })}
                />
              ))}
              {visibleCount < merged.length && (
                <button
                  onClick={() => setVisibleCount(prev => prev + PAGE_SIZE)}
                  className="w-full py-2.5 text-xs text-primary hover:text-primary/80 transition-colors"
                >
                  Show more ↓
                </button>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* SAB-1208. leagueId is the feed's own -- the modal never re-derives which league it
          is looking at, so it cannot disagree with the rows above it. */}
      <NFLWaiverClaimModal
        leagueId={leagueId ?? null}
        playerId={claim?.playerId ?? null}
        runDate={claim?.runDate ?? null}
        open={claim != null}
        onOpenChange={o => { if (!o) setClaim(null); }}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Subcomponents                                                      */
/* ------------------------------------------------------------------ */

function EmptyFeed({ filter }: { filter: FilterType }) {
  const filterLabel = FILTER_TYPES.find(f => f.id === filter)?.label;
  return (
    <div className="flex flex-col items-center justify-center py-10 text-center px-4">
      <Filter className="w-8 h-8 text-muted-foreground/40 mb-3" />
      <p className="text-sm font-medium text-muted-foreground">
        {filter === 'all' ? 'No activity yet' : `No ${filterLabel} activity`}
      </p>
      <p className="text-xs text-muted-foreground/70 mt-1">
        {filter !== 'all'
          ? 'Try adjusting your filter'
          : 'League activity will appear here'}
      </p>
    </div>
  );
}

function FeedRow({
  item,
  freshlyAwarded,
  formatDate,
  onOpenWaiverClaim,
}: {
  item: FeedItem;
  freshlyAwarded: boolean;
  formatDate: (iso: string) => string;
  onOpenWaiverClaim: (playerId: string, runDate: string | null) => void;
}) {
  if (item.kind === 'bonus_award') {
    return <BonusAwardRow item={item} freshlyAwarded={freshlyAwarded} />;
  }
  if (item.kind === 'survivor_eliminated') {
    return <SurvivorEliminationRow item={item} />;
  }

  // Existing tx row (add_drop / trade / sabotage / generic)
  //
  // SAB-1055: the fallback used to be Add/Drop, which does not mean "unknown" -- it makes a
  // specific, confident, WRONG claim about anything not explicitly listed. That is how a
  // trade proposal shipped chipped as "Add/Drop", and prod also carries commissioner_reward,
  // draft_undo and draft_cancel rows all wearing the same wrong badge today.
  //
  // Add/Drop is now matched explicitly and the fallback is neutral. An unrecognised type
  // should look unrecognised, not like the last branch someone happened to write.
  const meta = ACTIVITY_BADGES[item.activityType] ?? {
    Icon: Activity, label: 'Activity', accent: 'text-muted-foreground', bg: 'bg-muted/40',
  };

  // SAB-1208: a waiver row opens its claim detail. Rendered as a real <button> rather than
  // an onClick on the div so it is reachable by keyboard and announced as actionable --
  // and NOT rendered as one when there is no claim behind it, because a control that looks
  // interactive and does nothing is the defect SAB-1045 and SAB-1062 both closed.
  const claimPlayerId = item.waiverPlayerId;
  const Wrapper: 'div' | 'button' = claimPlayerId ? 'button' : 'div';

  return (
    <Wrapper
      {...(claimPlayerId
        ? {
            type: 'button' as const,
            onClick: () => onOpenWaiverClaim(claimPlayerId, item.waiverRunDate),
            'data-testid': 'feed-waiver-row',
          }
        : {})}
      className={cn(
        'w-full text-left flex items-start gap-3 p-3.5 border-b border-border/40 last:border-0 transition-colors',
        claimPlayerId ? 'hover:bg-muted/40 cursor-pointer' : 'hover:bg-muted/20',
      )}
    >
      <div className={cn('w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5', meta.bg)}>
        <meta.Icon className={cn('h-3.5 w-3.5', meta.accent)} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 mb-0.5">
          <Badge variant="outline" className={cn('text-[9px] px-1.5 h-4', meta.accent)}>
            {meta.label}
          </Badge>
          {item.week != null && (
            <span className="text-[10px] text-muted-foreground">Wk {item.week}</span>
          )}
        </div>
        <p className="text-xs text-foreground font-medium leading-tight">{item.title}</p>
        <p className="text-[10px] text-muted-foreground mt-0.5 leading-snug">
          {item.description}
        </p>
      </div>
      <span className="text-[10px] text-muted-foreground shrink-0 mt-1">
        {formatDate(item.createdAt)}
      </span>
    </Wrapper>
  );
}

function BonusAwardRow({
  item,
  freshlyAwarded,
}: {
  item: Extract<FeedItem, { kind: 'bonus_award' }>;
  freshlyAwarded: boolean;
}) {
  return (
    <div
      className={cn(
        'relative p-3.5 border-b border-border/40 last:border-0 transition-colors',
        freshlyAwarded
          ? 'bg-gradient-to-br from-orange-500/12 to-amber-500/5 ring-1 ring-orange-500/50'
          : 'hover:bg-muted/20',
      )}
    >
      {freshlyAwarded && (
        <span className="absolute top-2 right-3 text-[9px] font-bold tracking-[0.1em] text-orange-500">
          JUST NOW
        </span>
      )}
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-amber-500/15 text-amber-400 flex items-center justify-center shrink-0">
          <BonusIcon name={item.icon} className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-bold uppercase tracking-[0.06em] text-muted-foreground">
            Bonus Awarded{item.awardedForWeek != null ? ` · Week ${item.awardedForWeek}` : ''}
          </div>
          <div className="font-bold text-sm mt-0.5 truncate">{item.bonusName}</div>
          <div className="flex items-center gap-2 mt-1.5">
            <span
              className="w-5 h-5 rounded-full flex items-center justify-center font-bold text-[9px]"
              style={{ background: teamColor(item.teamId), color: '#0B1322' }}
            >
              {teamAvatar(item.teamName)}
            </span>
            <span className="text-xs font-bold truncate">{item.teamName}</span>
            <span className="ml-auto font-bold text-base text-amber-400 tabular-nums">
              +{fmtUSD(item.amount)}
            </span>
          </div>
          {item.reason && (
            <div className="text-[11px] text-muted-foreground mt-1.5 italic leading-snug">
              {item.reason}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SurvivorEliminationRow({
  item,
}: {
  item: Extract<FeedItem, { kind: 'survivor_eliminated' }>;
}) {
  return (
    <div className="p-3.5 border-b border-border/40 last:border-0 hover:bg-muted/20 transition-colors">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-red-500/12 text-red-400 flex items-center justify-center shrink-0">
          <Skull className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-bold uppercase tracking-[0.06em] text-red-400">
            Survivor · Week {item.eliminatedWeek}
          </div>
          <div className="font-bold text-sm mt-0.5 truncate">
            {item.teamName} eliminated
          </div>
          {item.fantasyScore != null && (
            <div className="text-[11px] text-muted-foreground mt-0.5">
              {item.fantasyScore.toFixed(1)} pts, week's lowest active score
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function tsOf(item: FeedItem): string {
  if (item.kind === 'bonus_award') return item.awardedAt;
  if (item.kind === 'survivor_eliminated') return item.eliminatedAt;
  return item.createdAt;
}
